import { Field } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCheckoutState } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import { DynamicFieldAreaBaseProps, DynamicFieldProps, getEnabledFields } from '../../../utils/dynamicFields';
import getBirthDateValidator from '../../../utils/getBirthDateValidator';
import getCheckboxValidator from '../../../utils/getCheckboxValidator';
import getValidator from '../../../utils/getValidator';
import { getLinksForTranslation } from '../../atoms/Link';
import Paragraph from '../../atoms/Paragraph';
import SubHeadline from '../../atoms/SubHeadline';
import CheckboxField from '../../formik/CheckboxField';
import DateField from '../../formik/DateField';
import PhoneField from '../../formik/PhoneField';
import TextField from '../../formik/TextField';
import './style.scss';

type DynamicFieldAreaProps = DynamicFieldAreaBaseProps & {
  headlineI18nKey?: string;
};

const DynamicField = ({ type, dataTestId, name, i18nKeys, ...props }: DynamicFieldProps) => {
  const { formatMessage } = useIntl();
  if (type === 'birthDate') {
    return (
      <Field
        name={name}
        component={DateField}
        label={`${formatMessage({ id: 'address.birthDate' })} (${config.formats.date})`}
        validate={getBirthDateValidator(props)}
        data-test-id={dataTestId}
      />
    );
  } else if (['phoneNumber', 'logisticsPhoneNumber'].includes(type)) {
    return (
      <Field
        name={name}
        component={PhoneField}
        label={formatMessage({ id: 'address.phoneNumber' })}
        maxLength={20}
        validate={getValidator(props)}
        defaultCountry={['ZZ', 'AA'].includes(config.country) ? undefined : config.country}
        data-test-id={dataTestId}
      />
    );
  } else if (type === 'taxId') {
    return (
      <Field
        name={name}
        component={TextField}
        label={formatMessage({ id: 'address.taxId' })}
        maxLength={props.maxLength ?? 20}
        validate={getValidator({ format: 'default', ...props })}
        data-test-id={dataTestId}
      />
    );
  } else if (type === 'carrierCustomerId') {
    return (
      <Field
        name={name}
        component={TextField}
        label={formatMessage({ id: 'shipping.collectionPoint.location.additionalCustomerInfo.carrierCustomerId' })}
        maxLength={props.maxLength ?? 20}
        validate={getValidator({ format: 'default', ...props })}
        data-test-id={dataTestId}
      />
    );
  } else if (type === 'checkbox') {
    return (
      <Field
        name={name}
        component={CheckboxField}
        validate={getCheckboxValidator(props)}
        data-test-id={dataTestId}
        label={<FormattedMessage id={i18nKeys!.label} values={getLinksForTranslation()} />}
        headline={i18nKeys!.headline && <FormattedMessage id={i18nKeys!.headline} />}
      />
    );
  } else if (type === 'text') {
    return (
      <>
        {i18nKeys!.headline && (
          <SubHeadline>
            <FormattedMessage id={i18nKeys!.headline} />
          </SubHeadline>
        )}
        <Paragraph>
          <FormattedMessage id={i18nKeys!.label} values={getLinksForTranslation()} />
        </Paragraph>
      </>
    );
  } else {
    return null;
  }
};

const DynamicFieldArea = ({ area, context, conditionValues, headlineI18nKey }: DynamicFieldAreaProps) => {
  // This might exceptionally be an empty object here, as this component is also used in the auth iframe where there is no checkout state
  const { checkoutState } = useCheckoutState();

  const enabledFields = getEnabledFields({
    area,
    context,
    conditionValues: {
      ...conditionValues,
      basketTotal: checkoutState?.orderState.basket.total.costWithTaxWithVouchersWithServiceCostsWithGiftCard,
    },
    excludeNewsletterSignUp: checkoutState?.integrations.newsletter.customerIsConfirmedSubscriber,
  });

  if (!enabledFields.length) {
    return null;
  }

  return (
    <div className={`dynamic-field-area dynamic-field-area--${area}`}>
      {headlineI18nKey && (
        <p className="dynamic-field-area__headline">
          <FormattedMessage id={headlineI18nKey} />
        </p>
      )}
      {enabledFields.map(({ key, ...dynamicFieldProps }) => (
        <div className={`form__row dynamic-field-area__field dynamic-field-area__field--${key}`} key={key}>
          <div className="form__field-container">
            <DynamicField {...dynamicFieldProps} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default DynamicFieldArea;
